import { IconDotsVertical } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import React, { useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import {
    DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown,
} from 'reactstrap';
import { useQueryClient } from '@tanstack/react-query';

import { useAuthContext } from 'contexts/AuthContext';
import { IProForma } from 'queries/ProForma/useGetProFormasByDeal';
import RenderIf from 'components/RenderIf/RenderIf';
import { formatUTCtoLocal } from 'ns_libs/formatter';
import { NSBadge, NSCard, NSModal } from 'bricks';
import { URLS } from 'services/urls';
import EmptyState from 'components/EmptyState/EmptyState';
import { useDuplicateProForma, useHardDeleteProForma, useUpdateProForma } from 'queries/ProForma';
import SortingIcon from 'components/DraggableTable/DraggableHeader/SortingIcon/SortingIcon';
import NSTable from '../../../../bricks/NSTable/NSTable';
import {
    getSortingIconProps,
    sortItems,
    SortConfig,
} from '../../helpers';
import {
    headerCellClassName,
    LINKED_TO_PROJECT_BADGE_TEXT,
    PRO_FORMA_DELETE,
    PRO_FORMA_ELLIPSIS_OPTIONS,
    PRO_FORMA_STATUS,
    PRO_FORMA_STATUS_MAPPING,
    PRO_FORMA_SET_AS_ACTIVE,
    PRO_FORMAS_TABLE_HEADERS,
} from '../../constants';

export interface IDealAutomatedTableProps {
    proFormas: IProForma[];
    isLoading?: boolean;
    dealId?: number;
    isAllDealsProFormasPage?: boolean;
}

const DealAutomatedTable = ({
    proFormas,
    isLoading,
    dealId: dealIdProp,
    isAllDealsProFormasPage = false,
}: IDealAutomatedTableProps) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { selectedOrganizationId } = useAuthContext();
    const { dealId: dealIdFromParams } = useParams<{ dealId: string }>();
    const dealId = dealIdProp || Number(dealIdFromParams);

    const draftOrInProgressStatus = new Set([PRO_FORMA_STATUS.DRAFT, PRO_FORMA_STATUS.IN_PROGRESS]);

    const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
    const [proFormaToDelete, setProFormaToDelete] = useState<{ [key: string]: number }>({});
    const [sortedBy, setSortedBy] = useState<SortConfig | null>(null);

    const sortedProFormas = React.useMemo(() => sortItems(proFormas, sortedBy, sortedBy?.key || ''), [proFormas, sortedBy]);

    const { mutate: updateProForma } = useUpdateProForma();
    const handleUpdateProForma = (proFormaId: number, patchData: object) => {
        updateProForma(
            { dealId: Number(dealId), proFormaId, patchData },
            {
                onSuccess: () => queryClient.invalidateQueries({ queryKey: ['getProFormas'] }),
            },
        );
    };

    const { mutate: hardDeleteProForma } = useHardDeleteProForma();
    const handleHardDeleteProForma = async (proFormaId: number) => hardDeleteProForma({ dealId: Number(dealId), proFormaId });

    const { mutate: duplicateProForma } = useDuplicateProForma();
    const handleDuplicateProForma = async (proFormaId: number) => duplicateProForma({ organizationId: selectedOrganizationId!, proFormaId });

    const handleEllipsisOption = (option: string, proFormaId: number, proFormaStatusId: number) => {
        if (option === 'Duplicate') handleDuplicateProForma(proFormaId);
        if (option === 'Set as active') handleUpdateProForma(proFormaId, { isActive: true });
        if (option === 'Delete') {
            setProFormaToDelete({ id: proFormaId, proFormaStatusId });
            setShowDeleteConfirmationModal(true);
        }
    };

    const handleConfirmDelete = () => {
        if (draftOrInProgressStatus.has(proFormaToDelete?.proFormaStatusId)) handleHardDeleteProForma(proFormaToDelete?.id);
        else handleUpdateProForma(proFormaToDelete?.id, { deletedAt: new Date() });
        setShowDeleteConfirmationModal(false);
    };

    const getProFormaEllipsisOptions = (status: number, isActive: boolean, isLinkedToProject: boolean) => {
        const baseOptions = isLinkedToProject
            ? PRO_FORMA_ELLIPSIS_OPTIONS[status].filter(option => option !== PRO_FORMA_DELETE)
            : PRO_FORMA_ELLIPSIS_OPTIONS[status];
        if (!isActive && !draftOrInProgressStatus.has(status)) return [...baseOptions, PRO_FORMA_SET_AS_ACTIVE];
        return baseOptions;
    };

    const redirectToProForma = (proForma: IProForma) => {
        const path = generatePath(URLS.PRO_FORMA.TABLE, { dealId, proFormaId: proForma.id });
        navigate(path);
    };

    const getDropdownHeapClassName = (option: string) => ({
        Duplicate: 'heap-deal-pro-forma-home-duplicate',
        Delete: 'heap-deal-pro-forma-home-delete',
        'Set as active': 'heap-deal-pro-forma-home-set-as-active',
    }[option] || '');

    return (
        <>
            <RenderIf isTrue={proFormas?.length === 0} isLoading={isLoading}>
                <EmptyState
                    title="No pro formas created yet!"
                    className={isAllDealsProFormasPage ? 'pt-2 pb-1' : 'vh-100'}
                    titleClassName={isAllDealsProFormasPage ? 'text-muted h6' : undefined}
                />
            </RenderIf>
            <RenderIf isTrue={proFormas?.length !== 0}>
                <NSTable className="NSTable--no-footer">
                    <thead className="NSTable__thead">
                        <tr className="NSTable__thead__tr">
                            {PRO_FORMAS_TABLE_HEADERS.map(header => (
                                <th key={header.key} className="NSTable__thead__tr__th">
                                    <div className={headerCellClassName}>
                                        <span>{header.name}</span>
                                        <SortingIcon {...getSortingIconProps(header.key, sortedBy, setSortedBy)} />
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="NSTable__tbody">
                        {sortedProFormas.map(proForma => (
                            <tr key={proForma.id} className="NSTable__tbody__tr text-dark">
                                <td>
                                    <span role="presentation" className="text-primary cursor--pointer" onClick={() => redirectToProForma(proForma)}>
                                        {proForma.name}
                                    </span>
                                    <RenderIf isTrue={proForma.isActive}>
                                        <NSBadge className="ml-2" color="primary-lighten">
                                            Active
                                        </NSBadge>
                                    </RenderIf>
                                    <RenderIf isTrue={proForma.subprojectId != null}>
                                        <NSBadge className="ml-2" color="primary-lighten">
                                            {LINKED_TO_PROJECT_BADGE_TEXT}
                                        </NSBadge>
                                    </RenderIf>
                                </td>
                                <td>
                                    {proForma.proFormaModelId && proForma.proFormaModelName
                                        ? proForma.proFormaModelName?.charAt(0).toUpperCase()
                                        + proForma.proFormaModelName?.slice(1).toLowerCase()
                                        : 'n/a'}
                                </td>
                                <td>{proForma.boeScenarioId ? proForma.boeScenario?.name : 'n/a'}</td>
                                <td>
                                    {formatUTCtoLocal(proForma.updatedAt, 'M/d/yy h:mm a')}
                                    <span>{proForma.updatedBy && ` by ${proForma.updatedBy}`}</span>
                                </td>
                                <td>{formatUTCtoLocal(proForma.createdAt, 'M/d/yy')}</td>
                                <td>{proForma.createdBy || 'n/a'}</td>
                                <td className="d-flex justify-content-between align-items-center">
                                    <NSBadge color={PRO_FORMA_STATUS_MAPPING[proForma.proFormaStatusId].color}>
                                        {PRO_FORMA_STATUS_MAPPING[proForma.proFormaStatusId].name}
                                    </NSBadge>
                                    <RenderIf isTrue={!isAllDealsProFormasPage}>
                                        <UncontrolledDropdown>
                                            <DropdownToggle tag="div">
                                                <NSIcon icon={IconDotsVertical} className="cursor--pointer" role="button" />
                                            </DropdownToggle>
                                            <DropdownMenu container="body" className="p-0 m-0 bg-transparent z-index-100">
                                                <NSCard className="NSCard--level-5 shadow-none">
                                                    {getProFormaEllipsisOptions(
                                                        proForma.proFormaStatusId,
                                                        proForma.isActive,
                                                        proForma.subprojectId != null,
                                                    ).map(option => (
                                                        <DropdownItem
                                                            key={`${option}-${proForma.id}`}
                                                            onClick={() => handleEllipsisOption(option, proForma.id, proForma.proFormaStatusId)}
                                                            className={`text-dark ${getDropdownHeapClassName(option)}`}
                                                        >
                                                            {option}
                                                        </DropdownItem>
                                                    ))}
                                                </NSCard>
                                            </DropdownMenu>
                                        </UncontrolledDropdown>
                                    </RenderIf>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </NSTable>
            </RenderIf>
            <NSModal
                isOpen={showDeleteConfirmationModal}
                toggle={() => setShowDeleteConfirmationModal(false)}
                modalHeaderText="Delete pro forma"
                modalHeaderClassName="text-white bg-danger"
                modalFooterButtonText="Delete"
                modalFooterButtonColor="danger"
                modalFooterCancelButtonText="Cancel"
                modalFooterButtonFunction={handleConfirmDelete}
            >
                <div className="mb-1">
                    <div className="text-white">Are you sure you want to delete this pro forma?</div>
                </div>
            </NSModal>
        </>
    );
};

export default DealAutomatedTable;
